<template>
  <div class="position-relative" :class="{ 'shadow': item.is_hidden, 'item-unavailable': !item.is_hidden && parseInt(item.available_quantity ?? 0) === 0 }" role="button" @click="showDetailModal = true">

    <!-- Dropdown outside the dimmed wrapper to avoid stacking context trapping -->
    <div class="dropdown position-absolute top-0 end-0 p-1">
      <button class="btn btn-sm btn-outline-primary rounded-circle lh-1" data-bs-toggle="dropdown" aria-expanded="false" @click.stop>⋮</button>
      <ul class="dropdown-menu dropdown-menu-end" @click.stop>
        <cross-listing-gate>
          <li>
            <a class="dropdown-item" href="#" @click.stop.prevent="createOrder">Create order with this item</a>
          </li>
        </cross-listing-gate>
        <li><a class="dropdown-item" href="#" @click.stop.prevent="updateSource">Edit source details</a></li>
        <li><a class="dropdown-item" :href="`https://www.ebay.com/itm/${item.remote_id}`" target="_blank" @click.stop>View on eBay</a></li>
        <li><a class="dropdown-item" href="#" @click.stop.prevent="copyRemoteId">Copy Item ID</a></li>
        <li><hr class="dropdown-divider"></li>
        <li v-if="!item.is_hidden">
          <a class="dropdown-item text-danger" href="#" @click.stop.prevent="showHideConfirm = true">Hide item</a>
        </li>
        <li v-else>
          <a class="dropdown-item" href="#" @click.stop.prevent="unhideItem">Unhide item</a>
        </li>
      </ul>
    </div>

    <!-- Content wrapper — dimmed when hidden, no stacking context impact on dropdown -->
    <div class="d-flex" :class="{ 'bg-light': item.is_hidden }">
      <div class="p-3 border-end" :class="{'d-block':showBox, 'd-none':!showBox, 'bg-success-subtle':checked}" role="button" @click.stop.prevent="selectItem">
        <i class="bi" :class="{'bi-check-circle':checked, 'bi-circle':!checked}"></i>
      </div>
      <div class="image-wrapper">
        <lazy-image :image-src="item.thumbnail || '/images/logo.svg'"
                    class="card-img me-3"
                    :title="item.title"/>
      </div>
      <div class="card-body p-1 p-lg-2 pe-5">

        <div class="item-title">
          <span v-if="item.is_sold" class="badge bg-danger text-white me-2">SOLD</span>
          <span v-if="item.is_hidden" class="badge bg-secondary text-white me-2">HIDDEN</span>
          <span v-if="item.sku_group_count > 1" class="badge bg-warning text-dark me-2">{{ item.sku_group_count }} listings</span>
          <span class="fw-bold">{{ item.title }}</span>
        </div>

        <div class="small text-info">
          <span class="badge bg-white border border-info text-info me-2 badge-quantity">QTY: {{ parseInt(item.available_quantity ?? 0) }}</span>
          <span class="small">{{ item.SKU }}</span>
        </div>

        <div v-if="latestSale" class="sold-price-block">
          <div class="d-flex align-items-baseline gap-2">
            <span class="fw-bold"><money-block :dollar="latestSale.unit_price" class="d-inline" /></span>
            <span class="text-muted small">sold</span>
            <span v-if="item.sale_price && item.sale_price !== latestSale.unit_price" class="text-muted small text-decoration-line-through">${{ item.sale_price }}</span>
          </div>
          <div class="text-muted small d-flex align-items-center gap-2 flex-wrap">
            <span>{{ $localDateTime(latestSale.order_date).slice(0, 10) }}</span>
            <span v-if="item.days_listed != null">· {{ item.days_listed }}d listed</span>
            <order-id-link :order-id="latestSale.order_id" />
          </div>
        </div>
        <div v-else class="fw-bold">
          ${{ item.sale_price }}
        </div>

        <div class="mt-2 small">
          <small class="text-muted">
            COGS: <money-block :dollar="-item.source_price || '0.00'" class="d-inline-block me-2" />
            <span v-if="item.source">{{ item.source }}</span>
            <span v-if="item.source && item.source_location" class="text-muted"> • </span>
            <span v-if="item.source_location" class="text-muted">{{ item.source_location }}</span>
          </small>
        </div>

      </div>
    </div>

    <app-modal :modal-in="showSource" @close="showSource = false" modal-class="modal-md">
      <template #header>Source Details</template>
      <item-source-edit :item="item" class="mb-3" />
      <div class="p-5 mb-5"></div>
    </app-modal>

    <app-modal :modal-in="showHideConfirm" @close="showHideConfirm = false" modal-class="modal-sm">
      <template #header>Hide Item</template>
      <p>This hides the item from your main inventory list. You can always unhide it or view it using the "Show Hidden" filter.</p>
      <div class="d-flex gap-2 justify-content-end">
        <button class="btn btn-outline-primary" @click="showHideConfirm = false">Cancel</button>
        <button class="btn btn-danger" @click="hideItem">Hide</button>
      </div>
    </app-modal>

  </div>

  <inventory-detail-modal v-model="showDetailModal" :item="item" />
</template>

<script setup>
import { defineProps, defineEmits, ref, watch, toRef, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import LazyImage from "@/app/components/Utils/LazyImage.vue";
import InventoryDetailModal from "@/app/components/inventory/InventoryDetailModal.vue";
import MoneyBlock from "@/app/components/Utils/MoneyBlock.vue";
import ItemSourceEdit from "@/app/components/inventory/ItemSourceEdit.vue";
import AppModal from "@/app/components/Utils/AppModal.vue";
import OrderIdLink from "@/app/components/orders/OrderIdLink.vue";
import CrossListingGate from "@/app/components/Utils/CrossListingGate.vue";

const props = defineProps({
  item: Object,
  showSelectBox: Boolean,
  selected: {
    type: Boolean,
    default: false,
  }
})
const emit = defineEmits(['onSelected', 'onUpdated'])
const store = useStore()
const router = useRouter()
const route = useRoute()
const checked = ref(props.selected)
const showBox = toRef(props, 'showSelectBox');
// is_sold is an unordered array from the sold CTE query — find most recent by date
const latestSale = computed(() => {
  if (!Array.isArray(props.item?.is_sold) || props.item.is_sold.length === 0) return null
  return props.item.is_sold.reduce((latest, sale) =>
    !latest || sale.order_date > latest.order_date ? sale : latest, null)
})
const showDetailModal = ref(false);
const showSource = ref(false);
const showHideConfirm = ref(false);

function selectItem() {
  emit('onSelected', !checked.value);
}

function updateSource() {
  showSource.value = true
}

function copyRemoteId() {
  navigator.clipboard.writeText(props.item.remote_id)
}

function createOrder() {
  router.push({
    name: 'NewOrder',
    params: { storeKey: route.params.storeKey },
    state: {
      prefillItem: {
        itemid:             props.item.remote_id,
        SKU:                props.item.SKU,
        memo:               props.item.title,
        quantity:           1,
        unit_price:         parseFloat(props.item.sale_price || 0),
        cogs:               parseFloat(props.item.source_price || 0),
        photo_url:          props.item.thumbnail || '',
        available_quantity: parseInt(props.item.available_quantity ?? 0),
      }
    }
  })
}

async function hideItem() {
  showHideConfirm.value = false
  await store.dispatch('eshop/updateInventoryIsHidden', { ...props.item, is_hidden: true })
  emit('onUpdated')
}

async function unhideItem() {
  await store.dispatch('eshop/updateInventoryIsHidden', { ...props.item, is_hidden: false })
  emit('onUpdated')
}

watch(() => props.selected, () => {
  checked.value = props.selected;
})

</script>

<style lang="scss" scoped>
@import '@/assets/sass/_variables-esc.scss';
.card-img {
  width:140px;
  height: 140px;
}
.image-wrapper {
  position: relative;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  &::after {
    content: '\F1C5';
    font-family: 'bootstrap-icons';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 2rem;
    color: white;
    opacity: 0;
    transition: opacity 0.2s;
    text-shadow: 0 2px 4px rgba(0,0,0,0.5);
    pointer-events: none;
  }

  &:hover::after {
    opacity: 1;
  }
}
.item-unavailable {
  //background: $gray-100;
  opacity: 0.50;
  .badge-quantity {
    opacity: 0.5;
  }
}
@media(max-width: 720px) {
  .item-title {
    font-size: 0.8rem;
    line-height: 1.2;
  }
  .card-img {
    width: 80px; height: 80px;
  }
}
</style>
